import { RECAPTCHA_SITE_KEY } from "./config";

// reCAPTCHA Enterprise, score-based (invisible) so it never adds friction.
// The site key is an Enterprise score key; the backend verifies tokens via the
// classic siteverify endpoint using the key's "legacy secret key", so no
// Enterprise SDK is needed server-side. When no site key is configured (local
// dev), we send the sentinel "dev-bypass" and the server skips verification.
// On a genuine reCAPTCHA failure we send "" and let the backend decide.

interface GrecaptchaLike {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaLike & { enterprise?: GrecaptchaLike };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("recaptcha_load_failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/** Enterprise script exposes grecaptcha.enterprise; fall back to the classic
 *  namespace so a key/script mismatch degrades gracefully instead of throwing. */
function api(): GrecaptchaLike | undefined {
  return window.grecaptcha?.enterprise ?? window.grecaptcha;
}

/** Warm the script early so token retrieval at submit time is instant. */
export function preloadRecaptcha(): void {
  void loadScript().catch(() => {});
}

export async function getRecaptchaToken(action: string): Promise<string> {
  if (!RECAPTCHA_SITE_KEY) return "dev-bypass";
  try {
    await loadScript();
    const g = api();
    if (!g) return "";
    await new Promise<void>((r) => g.ready(() => r()));
    return await g.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    return "";
  }
}
